import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRight, ShareNetwork, Sparkle } from "@phosphor-icons/react";
import api from "../lib/api";
import ShareButtons from "../components/ShareButtons";
import CashForecastTeaser from "../components/CashForecastTeaser";

const fmt$ = (n) => `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

export default function SharedHealthCheck() {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get(`/public/cash-health-check/${shareId}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.status === 404 ? "Report not found." : "Failed to load report."));
  }, [shareId]);

  const scoreColor = (s) => s >= 80 ? "text-emerald-700" : s >= 60 ? "text-amber-700" : "text-[#D0021B]";

  const share = () => {
    const url = window.location.href;
    if (navigator.share) {
      navigator.share({ title: `SmartERP Cash Flow Health Check (${data?.health_score}/100)`, url });
    } else {
      navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <div className="text-center" data-testid="share-not-found">
          <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400 mb-3">/ ERROR_404</div>
          <h1 className="font-display text-3xl font-black mb-3">{error}</h1>
          <Link to="/cash-health-check" className="text-[#FF3E00] hover:underline">Run your own check →</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="min-h-screen flex items-center justify-center bg-zinc-50"><div className="font-mono text-xs text-zinc-500 pulse-dot">LOADING REPORT…</div></div>;
  }

  return (
    <div className="min-h-screen bg-zinc-50 bg-grid">
      <header className="border-b border-zinc-950 bg-white">
        <div className="max-w-4xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-3">
            <span className="font-mono text-[10px] tracking-[0.18em] text-zinc-400">SMARTERP</span>
            <span className="font-display text-2xl font-black tracking-tight">Enterprise.</span>
          </Link>
          <Link to="/cash-health-check" className="text-xs font-mono text-zinc-700 hover:text-[#FF3E00]">
            RUN YOUR OWN →
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="font-mono text-[10px] tracking-[0.2em] text-zinc-400 mb-2">/ SHARED_REPORT</div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">
          {data.company}&apos;s<br />
          <span className="text-[#FF3E00]">cash flow check.</span>
        </h1>

        <div className="mt-8 border border-zinc-950 bg-white animate-fade-up" data-testid="shared-report">
          <div className="border-b border-zinc-950 bg-zinc-950 text-white px-4 py-2.5 font-mono text-[11px] tracking-[0.2em] uppercase flex items-center justify-between">
            <span><Sparkle size={12} weight="fill" className="inline mr-1" /> health_check.report</span>
            <button onClick={share} className="text-[10px] font-mono hover:text-[#FF3E00] flex items-center gap-1" data-testid="share-btn">
              <ShareNetwork size={12} /> {copied ? "COPIED" : "SHARE"}
            </button>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 border-b border-zinc-950">
            <div className="border-r border-zinc-200 p-5 text-center">
              <div className="label-sm text-zinc-500">Health Score</div>
              <div className={`font-display font-black text-5xl tnum mt-1 ${scoreColor(data.health_score)}`} data-testid="shared-score">
                {data.health_score}
              </div>
              <div className="text-[10px] font-mono text-zinc-400 mt-1">/ 100</div>
            </div>
            <div className="border-r border-zinc-200 p-5 text-center">
              <div className="label-sm text-zinc-500">Net Monthly</div>
              <div className={`font-display font-black text-2xl tnum mt-1 ${(data.metrics?.net_monthly || 0) >= 0 ? "text-emerald-700" : "text-[#D0021B]"}`}>
                {fmt$(data.metrics?.net_monthly)}
              </div>
            </div>
            <div className="border-r border-zinc-200 p-5 text-center">
              <div className="label-sm text-zinc-500">Runway</div>
              <div className="font-display font-black text-2xl tnum mt-1">
                {(data.metrics?.runway_months || 0) >= 999 ? "∞" : `${data.metrics?.runway_months}mo`}
              </div>
            </div>
            <div className="p-5 text-center">
              <div className="label-sm text-zinc-500">AR / AP</div>
              <div className="font-display font-black text-2xl tnum mt-1">{data.metrics?.ar_ap_ratio ?? "—"}</div>
            </div>
          </div>

          <div className="p-6 text-sm leading-relaxed whitespace-pre-wrap text-zinc-950">
            {data.report}
          </div>

          <ShareButtons
            shareUrl={window.location.href}
            score={data.health_score}
            company={data.company}
          />

          {/* 7-day cash forecast teaser */}
          <CashForecastTeaser shareId={shareId} horizonDays={90} teaserDays={7} />

          <div className="border-t border-zinc-950 bg-zinc-50 px-6 py-5 flex flex-wrap items-center justify-between gap-3">
            <div className="text-xs text-zinc-600">Want this every day, across your whole business?</div>
            <Link to="/register" className="bg-[#FF3E00] text-white font-semibold text-xs px-5 py-2.5 hover:bg-zinc-950 transition-colors flex items-center gap-2">
              START FREE TRIAL <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        </div>

        <div className="mt-6 text-center text-xs text-zinc-500">
          Generated by SmartERP · Powered by Claude Sonnet 4.5 ·{" "}
          {data.created_at && new Date(data.created_at).toLocaleDateString()}
        </div>
      </div>
    </div>
  );
}
